import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check, Receipt, FileText } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

const products = [
  {
    icon: Receipt,
    title: "Purchase Invoice Discounting",
    what: "Fund your supplier payments without touching working capital. Your approved purchase invoices are discounted by a financier, who pays the supplier while you repay at agreed terms.",
    best: "Businesses that need to pay suppliers upfront but sell on credit.",
    points: [
      "Pay suppliers on time and negotiate better purchase terms",
      "Tenor typically linked to your sales collection cycle",
      "No hard collateral in many structures — invoices anchor the limit",
    ],
  },
  {
    icon: FileText,
    title: "Sales Invoice Discounting",
    what: "Unlock cash locked in receivables. Raise funds against invoices you've raised on creditworthy buyers, instead of waiting 30–90 days for payment.",
    best: "Businesses selling to corporates or government buyers on credit.",
    points: [
      "Convert receivables into cash within days of invoicing",
      "Limits grow with your sales — no repeated sanction cycles",
      "With or without recourse structures available",
    ],
  },
];

const steps = [
  { no: "01", title: "Share your invoices", body: "Send us sample invoices, buyer details and your last 12 months of banking — we assess discountability within two working days." },
  { no: "02", title: "We structure the limit", body: "We approach the right financiers (banks, NBFCs, factors), negotiate pricing and get your limit sanctioned." },
  { no: "03", title: "Draw funds per invoice", body: "Upload invoices as you raise them; funds hit your account typically within 24–48 hours of acceptance." },
];

const eligibility = [
  "Business vintage of 2–3 years or more",
  "GST-registered with consistent filing history",
  "B2B invoices raised on creditworthy buyers",
  "Clean banking with no cheque returns in recent months",
  "Healthy buyer payment track record",
];

const documents = [
  "KYC of the firm and promoters/directors",
  "GST registration and last 6–12 months of returns",
  "Bank statements for the last 12 months",
  "Financial statements / ITRs for the last 2–3 years",
  "Sample invoices, purchase orders and buyer agreements",
  "Debtor ageing and sales ledger",
  "Existing loan sanction letters, if any",
];

export default function InvoiceDiscounting() {
  return (
    <div data-testid="invoice-discounting-page" className="min-h-screen bg-night text-foreground">
      <header className="border-b border-white/10 bg-night/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="idp-brand" className="flex items-center">
            <span className="bg-white rounded-md px-3 py-1.5 flex items-center shadow-md shadow-black/30">
              <img src="/globus-lockup.png" alt="Globus Financial Services" className="h-7 w-auto object-contain" />
            </span>
          </Link>
          <Link data-testid="idp-back-link" to="/" className="text-sm text-[#A0AAB2] hover:text-gold transition-colors flex items-center gap-2">
            <ArrowLeft size={14} /> Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10">
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="py-20"
        >
          <p className="overline-label mb-4">Working Capital, Unlocked</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl leading-[1.08]">
            Your invoices can pay you <span className="text-gold italic">today.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-[#A0AAB2] leading-relaxed">
            Invoice discounting turns your trade invoices into immediate cash flow — whether you need to
            pay suppliers or unlock your own receivables. We structure and place both.
          </p>
        </motion.section>

        <section className="grid md:grid-cols-2 gap-8 pb-20">
          {products.map((p, i) => (
            <motion.article
              key={p.title}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.1, ease }}
              data-testid={`idp-product-${i}`}
              className="bg-slate2 border border-white/10 p-8"
            >
              <p.icon size={26} className="text-gold mb-6" />
              <h2 className="font-serif text-2xl sm:text-3xl mb-4">{p.title}</h2>
              <p className="text-[#A0AAB2] leading-relaxed text-sm md:text-base">{p.what}</p>
              <p className="mt-4 text-sm text-muted-foreground"><span className="text-gold">Best for:</span> {p.best}</p>
              <ul className="mt-6 space-y-3">
                {p.points.map((pt) => (
                  <li key={pt} className="flex gap-3 text-sm text-foreground/90">
                    <Check size={16} className="text-gold shrink-0 mt-0.5" /> {pt}
                  </li>
                ))}
              </ul>
            </motion.article>
          ))}
        </section>

        <section className="py-16 border-t border-white/10">
          <p className="overline-label mb-10">How it works</p>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <motion.div
                key={s.no}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.1, ease }}
                data-testid={`idp-step-${s.no}`}
              >
                <span className="font-serif text-5xl text-gold/30">{s.no}</span>
                <h3 className="font-serif text-xl sm:text-2xl mt-4 mb-3">{s.title}</h3>
                <p className="text-sm text-[#A0AAB2] leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="py-16 border-t border-white/10 grid md:grid-cols-2 gap-12">
          <div>
            <p className="overline-label mb-6">Who qualifies</p>
            <ul className="space-y-4">
              {eligibility.map((e) => (
                <li key={e} className="flex gap-3 text-sm md:text-base text-foreground/90">
                  <Check size={18} className="text-gold shrink-0 mt-0.5" /> {e}
                </li>
              ))}
            </ul>
          </div>
          <div data-testid="idp-documents">
            <p className="overline-label mb-6">Documents you'll need</p>
            <ul className="space-y-4">
              {documents.map((d) => (
                <li key={d} className="flex gap-3 text-sm md:text-base text-foreground/90">
                  <FileText size={16} className="text-gold shrink-0 mt-0.5" /> {d}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="py-16 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h2 className="font-serif text-2xl sm:text-3xl">Ready to discount your first invoice?</h2>
            <p className="text-sm text-muted-foreground mt-2">Share a few details — we'll respond within 1–2 business days.</p>
          </div>
          <Link
            data-testid="idp-enquire-cta"
            to="/#enquire"
            className="bg-gold text-night hover:bg-goldhover transition-colors rounded-sm px-7 py-3.5 font-semibold flex items-center gap-2 shrink-0"
          >
            Start an enquiry <ArrowUpRight size={16} />
          </Link>
        </section>
      </main>

      <footer className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-6 text-xs text-muted-foreground">
          © {new Date().getFullYear()} Globus Financial Services · Sahibabad, Uttar Pradesh 201005, India · GSTIN: 09AEFPK4400F1ZW
        </div>
      </footer>
    </div>
  );
}
